import React from "react";
import {
  FIELD_TYPE,
  OVERRIDE_DECISIONS,
  SIMPLE_DECISIONS,
  FieldDecision,
  FormConfigField,
  WorkspaceTask,
} from "../shared/types";
import * as S from "../shared/styles";

interface FieldReviewTableProps {
  fields: FormConfigField[];
  isOverride: boolean;
  task: WorkspaceTask;
  decisions: Record<string, FieldDecision>;
  onDecide: (label: string, patch: Partial<FieldDecision>) => void;
  showError: boolean;
  disabled?: boolean;
}

const FieldReviewTable: React.FC<FieldReviewTableProps> = ({
  fields,
  isOverride,
  task,
  decisions,
  onDecide,
  showError,
  disabled = false,
}) => {
  const decisionOptions = isOverride
    ? OVERRIDE_DECISIONS.map((v) => ({
        value: v,
        label:
          v === "ai_correct"
            ? "AI Correct"
            : v === "human_correct"
              ? "Human Correct"
              : "Both Incorrect",
      }))
    : SIMPLE_DECISIONS.map((v) => ({
        value: v,
        label: v === "correct" ? "Correct" : "Incorrect",
      }));

  let lastQuestion: string | null = null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
      {fields.map((f) => {
        const d = decisions[f.label] || {};
        const needsCorrection = d.decision === "incorrect" || d.decision === "both_incorrect";
        const missingDecision = f.mandatory && !d.decision;
        const missingCorrection = needsCorrection && !d.correction_value;
        const hasError = showError && (missingDecision || missingCorrection);

        const question = f.question?.trim() || null;
        const showQuestion = question !== null && question !== lastQuestion;
        if (question !== null) lastQuestion = question;
        else lastQuestion = null;

        return (
          <React.Fragment key={f.id}>
            {showQuestion && <div style={questionBanner}>{question}</div>}
            <div style={{ ...fieldCard, ...(hasError ? { borderColor: "#fca5a5" } : {}) }}>
              {/* -- Row 1: field label -- */}
              <div style={fieldHeader}>
                <span style={{ ...fieldLabel, color: hasError ? "#ef4444" : "#374151" }}>
                  {f.label}
                  {f.mandatory && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
                </span>
              </div>

              {/* -- Row 2: AI value (+ human value in override mode) -- */}
              {f.type === FIELD_TYPE.CHECKBOX ? (
                <CheckboxValue
                  label={f.options[0]?.trim() || f.label}
                  checked={
                    String(task.ai_generated_values?.[f.label] ?? "").toLowerCase() === "true"
                  }
                />
              ) : (
                <div style={valuesRow}>
                  <div style={valueBlock}>
                    <div style={valueBox}>{task.ai_generated_values?.[f.label] ?? "\u2014"}</div>
                  </div>
                  {isOverride && (
                    <div style={valueBlock}>
                      <div style={{ ...valueLabel, color: "#b45309" }}>Austin output</div>
                      <div style={{ ...valueBox, background: "#fef3c7", borderColor: "#fde68a" }}>
                        {task.human_override_values?.[f.label] ?? "\u2014"}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* -- Row 3: Review decision -- */}
              <div style={decisionRow}>
                <div style={decisionLabel}>Review decision</div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {decisionOptions.map((o) => {
                    const selected = d.decision === o.value;
                    return (
                      <label
                        key={o.value}
                        style={{
                          ...pillLabel,
                          ...(selected ? pillSelected : pillDefault),
                          ...(disabled ? { cursor: "default", opacity: 0.7 } : {}),
                          ...(missingDecision && showError && !selected
                            ? { borderColor: "#fca5a5" }
                            : {}),
                        }}
                      >
                        <input
                          type="radio"
                          name={`decision-${f.id}`}
                          value={o.value}
                          checked={selected}
                          disabled={disabled}
                          onChange={() => {
                            const nextNeedsCorrection =
                              o.value === "incorrect" || o.value === "both_incorrect";
                            onDecide(
                              f.label,
                              nextNeedsCorrection
                                ? { decision: o.value }
                                : {
                                    decision: o.value,
                                    correction_value: undefined,
                                    flagged: undefined,
                                  },
                            );
                          }}
                          style={{ display: "none" }}
                        />
                        {o.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* -- Row 4: Correction (only when Incorrect) -- */}
              {needsCorrection && (
                <div style={correctionSection}>
                  <CorrectionInput
                    field={f}
                    value={d.correction_value ?? ""}
                    disabled={disabled}
                    hasError={missingCorrection && showError}
                    onChange={(v) => onDecide(f.label, { correction_value: v })}
                  />

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "2px 0",
                      cursor: disabled ? "default" : "pointer",
                      fontSize: "0.8125rem",
                      color: "#374151",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!d.flagged}
                      disabled={disabled}
                      onChange={(e) => onDecide(f.label, { flagged: e.target.checked })}
                      style={{ width: "15px", height: "15px", accentColor: "#0f7b8a" }}
                    />
                    Flag this record for data or guideline follow-up
                  </label>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ----------------------------  CorrectionInput  ----------------------------

interface CorrectionInputProps {
  field: FormConfigField;
  value: string;
  disabled: boolean;
  hasError: boolean;
  onChange: (value: string) => void;
}

const CorrectionInput: React.FC<CorrectionInputProps> = ({
  field,
  value,
  disabled,
  hasError,
  onChange,
}) => {
  const errBorder = hasError ? "#ef4444" : "#d1d5db";
  const errColor = hasError ? "#ef4444" : "#6b7280";

  switch (field.type) {
    // -- Dropdown single: radio buttons from field.options --
    case FIELD_TYPE.DROPDOWN_SINGLE: {
      const opts = field.options.filter(Boolean);
      return (
        <fieldset
          style={{
            border: `1px solid ${errBorder}`,
            borderRadius: "6px",
            padding: "8px 10px",
            margin: 0,
          }}
        >
          <legend
            style={{ fontSize: "0.75rem", fontWeight: 600, color: errColor, padding: "0 4px" }}
          >
            Correct value
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label
                key={o}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  padding: "3px 0",
                  fontSize: "0.8125rem",
                  color: "#374151",
                  cursor: disabled ? "default" : "pointer",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="radio"
                  name={`correction-${field.id}`}
                  value={o}
                  checked={value === o}
                  disabled={disabled}
                  onChange={() => onChange(o)}
                  style={{ marginTop: "3px", flexShrink: 0 }}
                />
                <span style={{ wordBreak: "break-word" }}>{o}</span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    }

    // -- Dropdown multi: checkboxes from field.options --
    case FIELD_TYPE.DROPDOWN_MULTI: {
      const opts = field.options.filter(Boolean);
      const selected = value ? value.split("||") : [];
      const toggle = (o: string) => {
        const next = selected.includes(o) ? selected.filter((s) => s !== o) : [...selected, o];
        onChange(next.join("||"));
      };
      return (
        <fieldset
          style={{
            border: `1px solid ${errBorder}`,
            borderRadius: "6px",
            padding: "8px 10px",
            margin: 0,
          }}
        >
          <legend
            style={{ fontSize: "0.75rem", fontWeight: 600, color: errColor, padding: "0 4px" }}
          >
            Correct value(s)
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label
                key={o}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  padding: "3px 0",
                  fontSize: "0.8125rem",
                  color: "#374151",
                  cursor: disabled ? "default" : "pointer",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  disabled={disabled}
                  onChange={() => toggle(o)}
                  style={{ marginTop: "3px", flexShrink: 0 }}
                />
                <span style={{ wordBreak: "break-word" }}>{o}</span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    }

    // -- Numeric: number input with min/max/step --
    case FIELD_TYPE.NUMERIC:
      return (
        <div>
          <div
            style={{ fontSize: "0.75rem", fontWeight: 600, color: errColor, marginBottom: "4px" }}
          >
            Correct value
          </div>
          <input
            type="number"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter correct number"
            min={field.min ?? undefined}
            max={field.max ?? undefined}
            step={field.step ?? undefined}
            style={{
              ...S.formInput,
              borderColor: errBorder,
              width: "180px",
            }}
          />
        </div>
      );

    // -- Text / default: textarea --
    default:
      return (
        <div>
          <div
            style={{ fontSize: "0.75rem", fontWeight: 600, color: errColor, marginBottom: "4px" }}
          >
            Correct value
          </div>
          <textarea
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter the correct value or explain the correct interpretation"
            rows={2}
            style={{
              ...S.formInput,
              borderColor: errBorder,
              resize: "vertical" as const,
              minHeight: "48px",
            }}
          />
        </div>
      );
  }
};

// ----------------------------  CheckboxValue  ----------------------------

const CheckboxValue: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "6px 0",
      cursor: "default",
      fontSize: "0.8125rem",
      color: "#374151",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled
      style={{ width: "16px", height: "16px", accentColor: "#0f7b8a" }}
    />
    {label}
  </label>
);

// ----------------------------  Styles  ----------------------------

const questionBanner: React.CSSProperties = {
  padding: "10px 14px",
  fontWeight: 600,
  fontSize: "13px",
  color: "#1e3a5f",
  background: "#f0f7ff",
  borderRadius: "6px 6px 0 0",
  border: "1px solid #bfdbfe",
  borderBottom: "none",
};

const fieldCard: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "14px 16px",
  marginBottom: "8px",
  background: "white",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const fieldHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const fieldLabel: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.875rem",
};

const valuesRow: React.CSSProperties = {
  display: "flex",
  gap: "12px",
};

const valueBlock: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const valueLabel: React.CSSProperties = {
  fontSize: "0.7rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#0f7b8a",
  marginBottom: "4px",
};

const valueBox: React.CSSProperties = {
  background: "#f0fdfa",
  border: "1px solid #ccfbf1",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "0.8125rem",
  lineHeight: 1.6,
  color: "#1f2937",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: "300px",
  overflowY: "auto",
};

const decisionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const decisionLabel: React.CSSProperties = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#6b7280",
  flexShrink: 0,
};

const pillLabel: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 14px",
  borderRadius: "16px",
  fontSize: "0.8125rem",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.15s",
  border: "1px solid transparent",
  userSelect: "none",
};

const pillDefault: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#4b5563",
  borderColor: "#d1d5db",
};

const pillSelected: React.CSSProperties = {
  background: "#0f7b8a",
  color: "white",
  borderColor: "#0f7b8a",
};

const correctionSection: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  paddingTop: "4px",
  borderTop: "1px solid #f3f4f6",
};

export default FieldReviewTable;
